/**
 * Shared helpers for blog copy/download exports from CopyDownload.
 */

export function getSafeFilename(h1Selected) {
  return (h1Selected || 'blog').replace(/[^a-z0-9]/gi, '_').toLowerCase();
}

function escapeHtml(s) {
  if (!s) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Full HTML5 document for download or print-to-PDF; bodyHtml is editor HTML (trusted from Tiptap).
 */
export function buildHtmlDocument(title, bodyHtml, attributionPlain) {
  const safeTitle = escapeHtml(title || 'Blog');
  const att = (attributionPlain || '').trim().split('\n').map(escapeHtml).join('<br />');
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${safeTitle}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com" />
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
  <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Merriweather+Sans:wght@300;400;500;600;700;800&family=Hind+Siliguri:wght@300;400;500;600;700&display=swap" />
  <style>
    /* ── Tokens ─────────────────────────────────────────── */
    :root {
      --primary-text:  #202020;
      --muted-text:    #475569;
      --faint-text:    #64748b;
      --link-color:    #1a0dab;
      --navy:          #191836;
      --mint:          #9CF2D5;
      --font-heading:  'Merriweather Sans', system-ui, sans-serif;
      --font-body:     'Hind Siliguri', system-ui, sans-serif;
      --font-mono:     'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      --border:        #e2e8f0;
      --surface:       #f8fafc;
    }

    /* ── Reset & base ───────────────────────────────────── */
    *, *::before, *::after { box-sizing: border-box; }

    html { font-size: 16px; }

    body {
      font-family: var(--font-body);
      font-size: 1rem;
      line-height: 1.75;
      color: var(--primary-text);
      background: #ffffff;
      margin: 0;
      padding: 0;
    }

    /* ── Page wrapper ───────────────────────────────────── */
    .page {
      max-width: 54rem;
      margin: 0 auto;
      padding: 3rem 2rem 4rem;
    }

    /* ── Headings ───────────────────────────────────────── */
    article h1 {
      font-family: var(--font-heading);
      font-weight: 800;
      font-size: 2.75rem;
      line-height: 1.2;
      letter-spacing: -0.02em;
      color: var(--primary-text);
      margin: 0 0 1rem;
    }

    article h2 {
      font-family: var(--font-heading);
      font-weight: 700;
      font-size: 2rem;
      line-height: 1.3;
      letter-spacing: -0.01em;
      color: var(--primary-text);
      margin: 2.5rem 0 0;
      padding-bottom: 0.5rem;
      border-bottom: 2px solid #e8eaf0;
    }

    article h3 {
      font-family: var(--font-heading);
      font-weight: 700;
      font-size: 1.625rem;
      line-height: 1.3;
      color: var(--primary-text);
      margin: 2rem 0 0.5rem;
    }

    article h4 {
      font-family: var(--font-heading);
      font-weight: 600;
      font-size: 0.8125rem;
      line-height: 1.4;
      color: #334155;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      margin: 1.75rem 0 0.375rem;
    }

    article h5 {
      font-family: var(--font-heading);
      font-weight: 600;
      font-size: 1.125rem;
      line-height: 1.4;
      color: var(--primary-text);
      margin: 1.5rem 0 0.375rem;
    }

    article h6 {
      font-family: var(--font-heading);
      font-weight: 500;
      font-size: 1rem;
      line-height: 1.5;
      color: var(--muted-text);
      margin: 1.25rem 0 0.375rem;
    }

    /* ── Body text ──────────────────────────────────────── */
    article p {
      margin: 0 0 1.125rem;
      color: var(--primary-text);
    }

    article > p:first-of-type,
    article p.lead {
      font-weight: 500;
      font-size: 1.125rem;
      line-height: 1.6;
      color: #334155;
      margin-bottom: 2rem;
    }

    article strong { font-weight: 700; }
    article em     { font-style: italic; }

    /* ── Links ──────────────────────────────────────────── */
    article a {
      color: var(--link-color);
      font-weight: 500;
      text-decoration: underline;
      text-underline-offset: 3px;
      text-decoration-thickness: 1px;
      text-decoration-color: rgba(26, 13, 171, 0.35);
    }

    article a:hover {
      text-decoration-color: var(--link-color);
    }

    /* ── HR ─────────────────────────────────────────────── */
    article hr {
      border: none;
      height: 1px;
      background: linear-gradient(to right, transparent, #cbd5e1, transparent);
      margin: 3rem auto;
      width: 80%;
    }

    /* ── Section ────────────────────────────────────────── */
    article section { margin: 2rem 0; }

    /* ── Inline code ────────────────────────────────────── */
    article code {
      font-family: var(--font-mono);
      font-size: 0.875em;
      font-weight: 500;
      background: #f1f5f9;
      color: #be123c;
      padding: 0.15rem 0.4rem;
      border-radius: 4px;
      border: 1px solid var(--border);
    }

    /* ── Code blocks ────────────────────────────────────── */
    article pre {
      background: #0f172a;
      color: #e2e8f0;
      font-family: var(--font-mono);
      font-size: 0.875rem;
      line-height: 1.65;
      padding: 1.5rem 1.75rem;
      border-radius: 10px;
      margin: 2rem 0;
      overflow-x: auto;
      border: 1px solid #1e293b;
    }

    article pre code {
      background: none;
      color: inherit;
      padding: 0;
      border: none;
      font-size: inherit;
    }

    /* ── Lists ──────────────────────────────────────────── */
    article ul,
    article ol {
      margin: 1.25rem 0 1.5rem;
      padding-left: 1.75rem;
    }

    article li { margin-bottom: 0.5rem; }
    article ul li::marker { color: var(--link-color); }
    article ol li::marker { color: var(--link-color); font-weight: 600; }

    article ul ul,
    article ol ol,
    article ul ol,
    article ol ul { margin: 0.375rem 0 0.5rem; }

    /* ── Blockquote ─────────────────────────────────────── */
    article blockquote {
      font-weight: 500;
      font-size: 1.125rem;
      line-height: 1.6;
      color: #1e293b;
      font-style: italic;
      border-left: 3px solid var(--link-color);
      background: var(--surface);
      margin: 2.5rem 0;
      padding: 1.75rem 2rem 1.75rem 2.25rem;
      border-radius: 0 8px 8px 0;
      position: relative;
    }

    article blockquote::before {
      content: '\\201C';
      font-size: 4rem;
      color: var(--link-color);
      opacity: 0.15;
      position: absolute;
      top: -0.5rem;
      left: 1.25rem;
      line-height: 1;
      font-family: Georgia, serif;
      pointer-events: none;
    }

    article blockquote p { margin: 0; font-size: inherit; }

    /* ── Table of contents (nav) ────────────────────────── */
    article nav {
      background: var(--surface);
      border: 1px solid var(--border);
      border-left: 4px solid var(--navy);
      border-radius: 0 8px 8px 0;
      padding: 1.25rem 1.5rem;
      margin: 2rem 0;
      font-size: 0.875rem;
    }

    article nav::before {
      content: 'Table of Contents';
      display: block;
      font-family: var(--font-heading);
      font-weight: 700;
      font-size: 0.75rem;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--muted-text);
      margin-bottom: 0.75rem;
    }

    article nav ol,
    article nav ul { margin: 0; padding-left: 1.25rem; }
    article nav li { margin-bottom: 0.25rem; line-height: 1.5; }
    article nav a  { color: var(--link-color); font-weight: 500; text-decoration: none; }
    article nav a:hover { text-decoration: underline; }

    /* ── Tables ─────────────────────────────────────────── */
    article table {
      width: 100%;
      border-collapse: collapse;
      margin: 2rem 0;
      font-size: 0.9375rem;
      line-height: 1.5;
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 1px 4px rgba(0,0,0,0.06), 0 0 0 1px var(--border);
    }

    article thead { background: var(--navy); color: #ffffff; }

    article thead th,
    article th {
      padding: 0.875rem 1rem;
      font-family: var(--font-heading);
      font-weight: 600;
      font-size: 0.8125rem;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      color: #ffffff;
      text-align: left;
      border: none;
    }

    article tbody tr { border-bottom: 1px solid var(--border); }
    article tbody tr:last-child { border-bottom: none; }
    article tbody tr:nth-child(even) { background: var(--surface); }

    article table td,
    article table th {
      padding: 0.75rem 1rem;
      color: var(--primary-text);
      vertical-align: top;
      border: none;
    }

    article table td p,
    article table th p {
      margin: 0;
      font-size: inherit;
      line-height: inherit;
    }

    article tfoot td {
      padding: 0.75rem 1rem;
      font-weight: 600;
      background: #f1f5f9;
      border-top: 2px solid var(--border);
      font-size: 0.875rem;
      color: var(--muted-text);
    }

    /* ── Images & figures ───────────────────────────────── */
    article img {
      max-width: 100%;
      height: auto;
      border-radius: 8px;
      display: block;
      margin: 0 auto;
    }

    article figure { margin: 2.5rem 0; text-align: center; }

    article figure img {
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0,0,0,0.08);
    }

    article figcaption {
      margin-top: 0.75rem;
      font-size: 0.875rem;
      color: var(--faint-text);
      font-style: italic;
    }

    /* ── CTA elements ───────────────────────────────────── */
    article .cta-button {
      display: inline-block;
      font-family: var(--font-body);
      font-weight: 600;
      font-size: 1rem;
      color: #ffffff;
      background: linear-gradient(135deg, var(--link-color) 0%, #4338ca 100%);
      padding: 0.875rem 2rem;
      border-radius: 6px;
      text-decoration: none;
      margin: 1.75rem 0;
      letter-spacing: 0.01em;
      box-shadow: 0 4px 12px rgba(26,13,171,0.28);
    }

    article .cta-banner {
      font-family: var(--font-body);
      font-weight: 500;
      font-size: 1.125rem;
      line-height: 1.6;
      background: linear-gradient(135deg, var(--navy) 0%, #1e3a5f 100%);
      color: #ffffff;
      padding: 2rem 2.5rem;
      margin: 2.5rem 0;
      border-radius: 10px;
      text-align: center;
      border: 1px solid rgba(156,242,213,0.15);
    }

    article .cta-banner a { color: var(--mint); }

    /* ── Highlight ──────────────────────────────────────── */
    article mark {
      background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
      color: #92400e;
      padding: 0.1rem 0.3rem;
      border-radius: 3px;
    }

    /* ── Placeholders ───────────────────────────────────── */
    article .visual-placeholder,
    article .data-viz-placeholder {
      background: var(--surface);
      border: 2px dashed #cbd5e1;
      border-radius: 10px;
      padding: 2.5rem;
      margin: 2rem 0;
      text-align: center;
      color: var(--faint-text);
      font-style: italic;
      font-size: 0.875rem;
    }

    /* ── Attribution footer ─────────────────────────────── */
    footer.attribution {
      margin-top: 3rem;
      padding-top: 1.25rem;
      border-top: 1px solid var(--border);
      font-size: 0.8125rem;
      line-height: 1.6;
      color: var(--faint-text);
    }

    footer.attribution p { margin: 0; }

    /* ── Print ──────────────────────────────────────────── */
    @media print {
      body { padding: 0; }
      .page { padding: 1.5rem 1rem; max-width: 100%; }

      article h1, article h2, article h3 { page-break-after: avoid; }
      article table, article figure, article pre,
      article blockquote { page-break-inside: avoid; }

      article a { text-decoration: underline; color: var(--link-color); }
      article .cta-button {
        border: 2px solid var(--link-color);
        color: var(--link-color);
        background: none;
        box-shadow: none;
      }
      article .cta-banner {
        background: #f1f5f9;
        color: var(--primary-text);
        border: 1px solid var(--border);
      }
    }

    /* ── Responsive ─────────────────────────────────────── */
    @media (max-width: 640px) {
      .page { padding: 1.5rem 1rem 3rem; }
      article h1 { font-size: 2rem; }
      article h2 { font-size: 1.5rem; margin-top: 2rem; }
      article h3 { font-size: 1.25rem; }
      article table {
        display: block;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
      }
      article .cta-button { display: block; text-align: center; }
      article .cta-banner { padding: 1.5rem; }
    }
  </style>
</head>
<body>
  <div class="page">
    <article>${bodyHtml}</article>
    <footer class="attribution"><p>${att}</p></footer>
  </div>
</body>
</html>`;
}

export function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function openPrintableHtmlForPdf(htmlString) {
  const w = window.open('', '_blank');
  if (!w) {
    return false;
  }
  w.document.open();
  w.document.write(htmlString);
  w.document.close();
  const doPrint = () => {
    try {
      w.focus();
      w.print();
    } catch (_) {
      /* ignore */
    }
  };
  if (w.document.readyState === 'complete') {
    setTimeout(doPrint, 100);
  } else {
    w.onload = () => setTimeout(doPrint, 100);
  }
  return true;
}
